/**
 * ImportWizard — 5-step modal for importing leads from CSV or XLSX.
 *
 * Step 1 — pick    : File selection (drag & drop or browse)
 * Step 2 — map     : Column mapping with save/load templates
 * Step 3 — dupes   : Duplicate review — per-record or bulk action
 * Step 4 — options : Default source + confirm
 * Step 5 — result  : Success count + per-row error table
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import readXlsxFile from 'read-excel-file/browser'
import {
  Upload, X, ChevronRight, ChevronLeft, Save, Trash2,
  CheckCircle, AlertCircle, AlertTriangle, FileSpreadsheet,
  RefreshCw, Copy, SkipForward, Loader,
} from 'lucide-react'
import { leadService } from '../services/leads'
import type { MappingTemplate, DuplicateMatch } from '../../../types'
import { LeadSource } from '../../../types'
import { ModalOverlay } from '../../../components/ModalOverlay'
import { useCurrentUser } from '../../../hooks/useCurrentUser'

// ── System fields ─────────────────────────────────────────────────────────────

const SYSTEM_FIELDS = [
  { value: '',                    label: '— skip this column —' },
  { value: 'customer_name',       label: 'Customer Name *' },
  { value: 'mobile',              label: 'Mobile *' },
  { value: 'vehicle_reg_no',      label: 'Vehicle Registration No' },
  { value: 'vehicle_chassis_no',  label: 'Vehicle Chassis No' },
  { value: 'vehicle_model',       label: 'Vehicle Model' },
  { value: 'insurance_due_date',  label: 'Insurance Due Date' },
  { value: 'lead_source',         label: 'Lead Source' },
  { value: 'policy_number',       label: 'Policy Number (historical only)' },
  { value: 'policy_issued_at',    label: 'Policy Issued Date (historical only)' },
  { value: 'language_preference', label: 'Language' },
  { value: 'customer_notes',      label: 'Notes' },
  { value: 'dnd_status',          label: 'DND Status' },
]

// ── Column aliases (mirrors backend) ─────────────────────────────────────────

const ALIAS_MAP: Record<string, string> = {
  'customer name': 'customer_name', 'name': 'customer_name', 'customer_name': 'customer_name',
  'client name': 'customer_name', 'owner name': 'customer_name',
  'mobile': 'mobile', 'phone': 'mobile', 'contact': 'mobile',
  'phone number': 'mobile', 'mobile number': 'mobile', 'mobile no': 'mobile',
  'contact no': 'mobile', 'contact number': 'mobile', 'phone no': 'mobile',
  'mob no': 'mobile', 'mob': 'mobile',
  'vehicle no': 'vehicle_reg_no', 'vehicle number': 'vehicle_reg_no',
  'vehicle registration no': 'vehicle_reg_no', 'vehicle registration number': 'vehicle_reg_no',
  'registration no': 'vehicle_reg_no', 'registration': 'vehicle_reg_no',
  'reg no': 'vehicle_reg_no', 'reg number': 'vehicle_reg_no', 'vehicle_reg_no': 'vehicle_reg_no',
  'chassis no': 'vehicle_chassis_no', 'chassis': 'vehicle_chassis_no',
  'chassis number': 'vehicle_chassis_no', 'vehicle_chassis_no': 'vehicle_chassis_no',
  'model': 'vehicle_model', 'vehicle model': 'vehicle_model',
  'vehicle_model': 'vehicle_model', 'vehicle': 'vehicle_model', 'car model': 'vehicle_model',
  'due date': 'insurance_due_date', 'insurance due': 'insurance_due_date',
  'insurance due date': 'insurance_due_date', 'insurance_due_date': 'insurance_due_date',
  'expiry date': 'insurance_due_date', 'renewal date': 'insurance_due_date',
  'policy expiry': 'insurance_due_date', 'policy expiry date': 'insurance_due_date',
  'next follow up date': 'insurance_due_date', 'rsd': 'insurance_due_date',
  'source': 'lead_source', 'lead source': 'lead_source', 'lead_source': 'lead_source',
  'policy number': 'policy_number', 'policy no': 'policy_number', 'policy no.': 'policy_number',
  'policy_number': 'policy_number',
  'policy issued date': 'policy_issued_at', 'policy issue date': 'policy_issued_at',
  'date of issue': 'policy_issued_at', 'policy_issued_at': 'policy_issued_at',
  'language': 'language_preference',
  'notes': 'customer_notes', 'remarks': 'customer_notes',
  'dnd': 'dnd_status', 'dnd status': 'dnd_status',
}

function autoDetect(header: string): string {
  const key = header.toLowerCase().trim().replace(/[.:,;]+$/, '')
  return ALIAS_MAP[key] ?? ''
}

function looksLikeMetadataRow(row: unknown[]): boolean {
  const nonEmpty = row.filter(c => c !== null && c !== undefined && String(c).trim() !== '')
  if (nonEmpty.length === 0) return true
  if (1 - nonEmpty.length / row.length > 0.5) return true
  const first = String(nonEmpty[0]).trim()
  if (/^\d{1,2}[-/]\d{1,2}[-/]\d{2,4}$/.test(first)) return true
  if (/^\d+$/.test(first)) return true
  return false
}

// ── Dedup action labels ───────────────────────────────────────────────────────

type DedupAction = 'skip' | 'update' | 'create'

const ACTION_LABELS: Record<DedupAction, { label: string; icon: React.ReactNode; colour: string }> = {
  skip:   { label: 'Skip',        icon: <SkipForward className="h-3.5 w-3.5" />, colour: 'var(--text-tertiary)' },
  update: { label: 'Update',      icon: <RefreshCw   className="h-3.5 w-3.5" />, colour: 'var(--primary)' },
  create: { label: 'Create new',  icon: <Copy        className="h-3.5 w-3.5" />, colour: 'var(--success-text)' },
}

// ── Props / types ─────────────────────────────────────────────────────────────

interface Props {
  onClose: () => void
  onComplete: () => void
}

type Step = 'pick' | 'map' | 'dupes' | 'options' | 'result'

interface UploadResult {
  batch_id: number; total: number; success: number
  failures: Array<{ row: number; error: string }>
  warnings?: string[]
}

// ── XLSX row cache helper ─────────────────────────────────────────────────────

type XlsxCache = React.MutableRefObject<{ fileKey: string; rows: unknown[][] } | null>

async function getXlsxRows(f: File, cache: XlsxCache): Promise<unknown[][]> {
  const fileKey = `${f.name}-${f.size}-${f.lastModified}`
  if (cache.current?.fileKey === fileKey) return cache.current.rows
  const sheets = (await readXlsxFile(f)) as unknown as Array<{ sheet: string; data: unknown[][] }>
  const target = sheets.length === 1
    ? sheets[0]
    : sheets.find(s => s.sheet.toLowerCase() === 'leads')
  if (!target) {
    throw new Error(`Sheet named "Leads" not found. Rename your data sheet to "Leads" and re-upload.`)
  }
  const rows = target?.data ?? []
  cache.current = { fileKey, rows }
  return rows
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function ImportWizard({ onClose, onComplete }: Props) {
  const currentUser = useCurrentUser()
  const customMappingEnabled = !!(currentUser?.tenant_feature_flags?.['custom_mapping_enabled'])

  const [step, setStep]               = useState<Step>('pick')
  const [file, setFile]               = useState<File | null>(null)
  const [headers, setHeaders]         = useState<string[]>([])
  const [headerRow, setHeaderRow]     = useState(0)
  const [previewRows, setPreviewRows] = useState<unknown[][]>([])
  const [mapping, setMapping]         = useState<Record<string, string>>({})
  const [defaultSource, setDefaultSource] = useState<LeadSource>(LeadSource.OEM_DUMP)
  const [dateFormat, setDateFormat]       = useState<'auto' | 'DMY' | 'MDY'>('auto')
  const [dragging, setDragging]       = useState(false)

  // Parsed rows (built once after mapping confirmed, reused for dupe check + upload)
  const [parsedRows, setParsedRows]   = useState<Record<string, unknown>[]>([])

  // Duplicate review state
  const [dupesLoading, setDupesLoading] = useState(false)
  const [dupes, setDupes]             = useState<DuplicateMatch[]>([])
  const [uniqueCount, setUniqueCount] = useState(0)
  // Per-row action: rowIndex → action
  const [rowActions, setRowActions]   = useState<Record<number, DedupAction>>({})
  // Bulk action applied to ALL duplicates at once
  const [bulkAction, setBulkAction]   = useState<DedupAction | ''>('')

  // Upload
  const [uploading, setUploading]     = useState(false)
  const [result, setResult]           = useState<UploadResult | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)

  // Templates
  // Loading state for file parsing (transition from pick to next step)
  const [parsing, setParsing]         = useState(false)

  const [templates, setTemplates]     = useState<MappingTemplate[]>([])
  const [templateName, setTemplateName] = useState('')
  const [savingTemplate, setSavingTemplate] = useState(false)
  const [showSaveForm, setShowSaveForm] = useState(false)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const xlsxRowCacheRef = useRef<{ fileKey: string; rows: unknown[][] } | null>(null)

  useEffect(() => {
    if (customMappingEnabled) {
      leadService.listMappingTemplates().then(setTemplates).catch(() => {})
    }
  }, [customMappingEnabled])

  // ── File parsing ───────────────────────────────────────────────────────────

  const parseHeaders = useCallback(async (f: File, overrideHeaderRow?: number) => {
    let allRows: unknown[][]
    if (f.name.toLowerCase().endsWith('.csv')) {
      const text = await f.text()
      allRows = text.split(/\r?\n/).slice(0, 5).map(line =>
        line.split(',').map(c => c.replace(/^"|"$/g, '').trim())
      ).filter(r => r.some(Boolean))
    } else {
      allRows = (await getXlsxRows(f, xlsxRowCacheRef)).slice(0, 5)
    }
    if (!allRows.length) return

    setPreviewRows(allRows.slice(0, 3))

    let detectedHeaderRow = overrideHeaderRow ?? headerRow
    if (overrideHeaderRow === undefined) {
      detectedHeaderRow = looksLikeMetadataRow(allRows[0] as unknown[]) ? 1 : 0
      setHeaderRow(detectedHeaderRow)
    }

    const rawHdrs = ((allRows[detectedHeaderRow] ?? []) as unknown[])
      .map(h => String(h ?? '').trim()).filter(Boolean)
    const seen: Record<string, number> = {}
    const hdrs = rawHdrs.map(h => {
      if (seen[h]) { seen[h]++; return `${h} (${seen[h]})` }
      seen[h] = 1; return h
    })
    setHeaders(hdrs)

    const auto: Record<string, string> = {}
    SYSTEM_FIELDS.filter(f => f.value).forEach(({ value: sf }) => {
      const match = hdrs.find(h => autoDetect(h) === sf)
      auto[sf] = match ?? ''
    })
    setMapping(auto)
    return { auto, headerRow: detectedHeaderRow }
  }, [headerRow])

  // ── Mapping → Duplicate check transition ─────────────────────────────────

  const proceedToDupeCheck = async (f?: File, localMapping?: Record<string, string>, localHeaderRow?: number) => {
    const target = f ?? file
    if (!target) return
    setDupesLoading(true)
    try {
      const rows = await buildParsedRows(target, localMapping, localHeaderRow)
      setParsedRows(rows)

      const checkPayload = rows.map((row, i) => ({
        row_index: i,
        mobile: String(row.mobile ?? ''),
        vehicle_reg_no: row.vehicle_reg_no ? String(row.vehicle_reg_no) : undefined,
        customer_name: row.customer_name ? String(row.customer_name) : undefined,
        policy_number: row.policy_number ? String(row.policy_number) : undefined,
      })).filter(r => r.mobile)

      const res = await leadService.checkDuplicates(checkPayload)
      setDupes(res.duplicates)
      setUniqueCount(res.unique_count)

      // Default action for every duplicate: update
      const defaults: Record<number, DedupAction> = {}
      res.duplicates.forEach(d => { defaults[d.row_index] = 'update' })
      setRowActions(defaults)
      setBulkAction('')

      setStep('dupes')
    } catch {
      setUploadError('Failed to check for duplicates. You can still continue.')
      setStep('dupes')
    } finally {
      setDupesLoading(false)
    }
  }

  const pickFile = async (f: File) => {
    setFile(f); setHeaderRow(0); setUploadError(null); setParsing(true);
    if (fileInputRef.current) fileInputRef.current.value = ''
    try {
      const parsed = await parseHeaders(f, undefined)
      if (!parsed) { setParsing(false); return }  // empty file / no rows
      if (customMappingEnabled) {
        setStep('map')
      } else {
        // Free tier: skip map step, go straight to dupes with auto-detected mapping
        await proceedToDupeCheck(f, parsed.auto, parsed.headerRow)
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to read file. Ensure it is a valid .xlsx or .csv file.'
      console.error('ImportWizard: pickFile error', err)
      setUploadError(msg)
    } finally {
      setParsing(false)
    }
  }

  // ── Build parsed rows from file + mapping ─────────────────────────────────

  const buildParsedRows = useCallback(async (f: File, localMapping?: Record<string, string>, overrideHeaderRow?: number): Promise<Record<string, unknown>[]> => {
    let allRows: unknown[][]
    if (f.name.toLowerCase().endsWith('.csv')) {
      const text = await f.text()
      allRows = text.split(/\r?\n/).map(line =>
        line.split(',').map(c => c.replace(/^"|"$/g, '').trim())
      ).filter(r => r.some(Boolean))
    } else {
      allRows = await getXlsxRows(f, xlsxRowCacheRef)
    }
    const hdrIdx = overrideHeaderRow ?? headerRow
    const _mapping = localMapping ?? mapping
    const hdrRow = (allRows[hdrIdx] ?? []) as string[]
    const dataRows = allRows.slice(hdrIdx + 1)
    const colIndex: Record<string, number> = {}
    hdrRow.forEach((h, i) => { colIndex[String(h ?? '').trim()] = i })
    return dataRows.map(row => {
      const out: Record<string, unknown> = {}
      Object.entries(_mapping).forEach(([sysField, fileCol]) => {
        if (fileCol && colIndex[fileCol] !== undefined) {
          const val = (row as unknown[])[colIndex[fileCol]]
          // XLSX returns date cells as Date objects; serialise to YYYY-MM-DD so the
          // backend date parser recognises them (it rejects the ISO timestamp form).
          out[sysField] = val instanceof Date
            ? `${val.getFullYear()}-${String(val.getMonth() + 1).padStart(2, '0')}-${String(val.getDate()).padStart(2, '0')}`
            : val ?? null
        }
      })
      if (!out.lead_source) out.lead_source = defaultSource
      return out
    }).filter(row => row.customer_name || row.mobile)
  }, [headerRow, mapping, defaultSource])

  // ── Bulk action applies to all dupes ─────────────────────────────────────

  const applyBulkAction = (action: DedupAction) => {
    setBulkAction(action)
    const next: Record<number, DedupAction> = {}
    dupes.forEach(d => { next[d.row_index] = action })
    setRowActions(next)
  }

  // ── Template helpers ──────────────────────────────────────────────────────

  const applyTemplate = (t: MappingTemplate) => {
    setMapping(prev => {
      const next = { ...prev }
      Object.entries(t.mapping).forEach(([sf, fc]) => {
        if (headers.includes(fc as string)) next[sf] = fc as string
      })
      return next
    })
  }

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) return
    setSavingTemplate(true)
    try {
      const saved = await leadService.saveMappingTemplate(templateName.trim(), mapping)
      setTemplates(prev => [...prev, saved])
      setTemplateName(''); setShowSaveForm(false)
    } finally { setSavingTemplate(false) }
  }

  // ── Upload ────────────────────────────────────────────────────────────────

  const handleUpload = async () => {
    if (!file) return
    setUploading(true); setUploadError(null)

    try {
      if (!parsedRows.length) return
      // Stamp per-row _dedup_mode for duplicates; unique rows get no stamp (backend default = skip, fine since they're new)
      const dupeIndexSet = new Set(dupes.map(d => d.row_index))
      const payload = parsedRows
        .map((row, i) => {
          if (!dupeIndexSet.has(i)) return row
          const action = rowActions[i] ?? 'skip'
          if (action === 'skip') return null
          return { ...row, _dedup_mode: action }
        })
        .filter(Boolean) as Record<string, unknown>[]

      const res = await leadService.bulkUpload(payload, 'skip', dateFormat)
      setResult(res)
      setStep('result')
      if (res.failures.length === 0) onComplete()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        || (err instanceof Error ? err.message : 'Upload failed')
      setUploadError(msg)
    } finally {
      setUploading(false)
    }
  }

  const mappingComplete = () => !!(mapping['customer_name'] && mapping['mobile'])

  // ── Step metadata ─────────────────────────────────────────────────────────

  const ALL_STEPS: Step[] = ['pick', 'map', 'dupes', 'options', 'result']
  const STEPS: Step[] = customMappingEnabled ? ALL_STEPS : ALL_STEPS.filter(s => s !== 'map')
  const STEP_LABELS: Record<Step, string> = {
    pick: 'Select File', map: 'Map Columns', dupes: 'Review Duplicates',
    options: 'Options', result: 'Result',
  }
  const stepIndex = STEPS.indexOf(step)

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <ModalOverlay onClose={onClose}>
      <div className="modal-panel-flex" style={{ maxWidth: 780 }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="modal-flex-header">
          <h2 className="flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4" /> Import Leads
          </h2>
          <button className="btn-icon" onClick={onClose}><X className="h-4 w-4" /></button>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-0 px-5 pt-4 pb-2 flex-wrap">
          {STEPS.filter(s => s !== 'result').map((s, i, arr) => (
            <div key={s} className="flex items-center">
              <div
                className="flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded"
                style={{
                  color: STEPS.indexOf(s) <= stepIndex ? 'var(--primary)' : 'var(--text-tertiary)',
                  background: s === step ? 'var(--primary-subtle)' : 'transparent',
                }}
              >
                <span className="inline-flex items-center justify-center rounded-full text-xs font-bold"
                  style={{ width: 20, height: 20,
                    background: STEPS.indexOf(s) <= stepIndex ? 'var(--primary)' : 'var(--border-default)',
                    color: STEPS.indexOf(s) <= stepIndex ? '#fff' : 'var(--text-tertiary)',
                  }}>{i + 1}</span>
                {STEP_LABELS[s]}
              </div>
              {i < arr.length - 1 && <div className="w-6 h-px mx-1" style={{ background: 'var(--border-default)' }} />}
            </div>
          ))}
        </div>

        {/* Body */}
        <div className="modal-flex-body space-y-4 pt-0">

          {/* ── STEP 1: Pick file ───────────────────────────────────────────── */}
          {step === 'pick' && (
            <div
              className="flex flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed p-10 cursor-pointer transition-colors"
              style={{ borderColor: dragging ? 'var(--primary)' : 'var(--border-default)', background: dragging ? 'var(--primary-subtle)' : 'transparent' }}
              onDragOver={e => { e.preventDefault(); setDragging(true) }}
              onDragLeave={() => setDragging(false)}
              onDrop={e => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files?.[0]; if (f && /\.(csv|xlsx)$/i.test(f.name)) pickFile(f) }}
              onClick={() => !parsing && fileInputRef.current?.click()}
            >
              {parsing ? (
                <div className="flex flex-col items-center gap-3 py-4">
                  <Loader className="h-8 w-8 animate-spin" style={{ color: 'var(--primary)' }} />
                  <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Reading file…</p>
                  <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>This may take a moment for large files.</p>
                </div>
              ) : (
                <>
                  <Upload className="h-10 w-10" style={{ color: 'var(--text-tertiary)' }} />
                  <div className="text-center">
                    <p className="font-medium" style={{ color: 'var(--text-primary)' }}>Drag & drop your file here, or click to browse</p>
                    <p className="text-sm mt-1" style={{ color: 'var(--text-tertiary)' }}>Supports .csv and .xlsx — up to 10 MB</p>
                  </div>
                </>
              )}
              <input ref={fileInputRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) pickFile(f) }} />
              {uploadError && (
                <div className="alert-danger">
                  <AlertCircle className="h-4 w-4 shrink-0" /><span>{uploadError}</span>
                </div>
              )}
            </div>
          )}

          {/* ── STEP 2: Map columns ─────────────────────────────────────────── */}
          {step === 'map' && customMappingEnabled && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                <FileSpreadsheet className="h-4 w-4" />
                <span className="font-medium">{file?.name}</span>
                <span style={{ color: 'var(--text-tertiary)' }}>— {headers.length} columns detected</span>
              </div>

              {/* Header row selector */}
              {previewRows.length > 1 && (
                <div className="p-3 rounded-lg flex items-start gap-3" style={{ background: 'var(--surface-raised)', border: '1px solid var(--border-default)' }}>
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" style={{ color: 'var(--warning-text)' }} />
                  <div className="flex-1 space-y-2">
                    <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Which row contains your column headers?</p>
                    <div className="flex flex-wrap gap-2">
                      {previewRows.map((row, i) => {
                        const preview = (row as unknown[]).filter(c => c != null && String(c).trim()).slice(0, 4).map(c => String(c).trim()).join(', ')
                        return (
                          <button key={i} onClick={() => { setHeaderRow(i); if (file) parseHeaders(file, i).catch(() => {}) }}
                            className={headerRow === i ? 'btn-primary' : 'btn-secondary'}
                            style={{ fontSize: 12, height: 30, padding: '0 10px' }}>
                            <span className="font-bold">Row {i + 1}</span>
                            {preview && <span className="ml-1 opacity-70">— {preview.slice(0, 50)}{preview.length > 50 ? '…' : ''}</span>}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Template toolbar */}
              <div className="flex flex-wrap items-center gap-2">
                {templates.length > 0 && (
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Load template:</span>
                    {templates.map(t => (
                      <div key={t.id} className="flex items-center gap-0.5">
                        <button className="btn-secondary" style={{ height: 26, fontSize: 11, padding: '0 8px' }} onClick={() => applyTemplate(t)}>{t.name}</button>
                        <button className="btn-icon" style={{ width: 22, height: 26 }} onClick={() => leadService.deleteMappingTemplate(t.id).then(() => setTemplates(p => p.filter(x => x.id !== t.id)))}>
                          <Trash2 className="h-3 w-3" style={{ color: 'var(--danger-text)' }} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <button className="btn-secondary ml-auto" style={{ height: 26, fontSize: 11, padding: '0 10px' }} onClick={() => setShowSaveForm(v => !v)}>
                  <Save className="h-3 w-3" /> Save as template
                </button>
              </div>

              {showSaveForm && (
                <div className="flex items-center gap-2 p-3 rounded-lg" style={{ background: 'var(--surface-raised)' }}>
                  <input className="input flex-1" style={{ height: 32 }} placeholder="Template name (e.g. OEM Dump — Bajaj)" value={templateName}
                    onChange={e => setTemplateName(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSaveTemplate()} />
                  <button className="btn-primary" style={{ height: 32, padding: '0 12px', fontSize: 12 }} disabled={!templateName.trim() || savingTemplate} onClick={handleSaveTemplate}>
                    {savingTemplate ? 'Saving…' : 'Save'}
                  </button>
                  <button className="btn-icon" onClick={() => setShowSaveForm(false)}><X className="h-3.5 w-3.5" /></button>
                </div>
              )}

              {/* Mapping table */}
              <div className="table-card overflow-hidden">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{ width: '40%' }}>System field</th>
                      <th style={{ width: '50%' }}>Column in your file</th>
                      <th style={{ width: '10%' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {SYSTEM_FIELDS.filter(f => f.value).map(({ value: sf, label }) => {
                      const fileCol = mapping[sf] ?? ''
                      const isRequired = label.endsWith('*')
                      const isAuto = fileCol !== '' && autoDetect(fileCol) === sf
                      return (
                        <tr key={sf}>
                          <td style={{ fontSize: 13 }}>
                            <span className={isRequired ? 'font-semibold' : ''}>{label.replace(' *', '')}</span>
                            {isRequired && <span style={{ color: 'var(--danger-text)', marginLeft: 2 }}>*</span>}
                          </td>
                          <td>
                            <select className="input" style={{ height: 30, fontSize: 12 }} value={fileCol}
                              onChange={e => setMapping(prev => ({ ...prev, [sf]: e.target.value }))}>
                              <option value="">— not in this file —</option>
                              {headers.map((h, i) => <option key={`${i}-${h}`} value={h}>{h}</option>)}
                            </select>
                          </td>
                          <td>{isAuto && <span className="badge-success text-xs">auto</span>}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {!mappingComplete() && (
                <div className="alert-warning">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  <span>Map at least <strong>Customer Name</strong> and <strong>Mobile</strong> to continue.</span>
                </div>
              )}
            </div>
          )}
          {step === 'map' && !customMappingEnabled && (
            <div className="flex flex-col items-center justify-center gap-4 py-12">
              <div className="p-4 rounded-full" style={{ background: 'var(--warning-subtle, #fffbeb)' }}>
                <AlertTriangle className="h-8 w-8" style={{ color: 'var(--warning-text, #b45309)' }} />
              </div>
              <div className="text-center max-w-sm">
                <h3 className="font-semibold text-lg" style={{ color: 'var(--text-primary)' }}>Custom Mapping</h3>
                <p className="text-sm mt-1" style={{ color: 'var(--text-tertiary)' }}>
                  Customise how your file columns map to system fields and save reusable templates
                  with the <strong>Custom Mapping</strong> add-on.
                </p>
              </div>
              <button className="btn-primary" onClick={onClose}>
                View Plans
              </button>
            </div>
          )}

          {/* ── STEP 3: Duplicate review ─────────────────────────────────────── */}
          {step === 'dupes' && (
            <div className="space-y-4">
              {dupesLoading ? (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <Loader className="h-8 w-8 animate-spin" style={{ color: 'var(--primary)' }} />
                  <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>Checking for duplicates…</p>
                </div>
              ) : (
                <>
                  {/* Summary banner */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="p-4 rounded-lg text-center" style={{ background: 'var(--surface-raised)', border: '1px solid var(--border-default)' }}>
                      <p className="text-2xl font-bold" style={{ color: 'var(--success-text)' }}>{uniqueCount}</p>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>New records — will be imported</p>
                    </div>
                    <div className="p-4 rounded-lg text-center" style={{ background: dupes.length ? 'var(--warning-subtle, #fffbeb)' : 'var(--surface-raised)', border: `1px solid ${dupes.length ? 'var(--warning-border, #fcd34d)' : 'var(--border-default)'}` }}>
                      <p className="text-2xl font-bold" style={{ color: dupes.length ? 'var(--warning-text, #b45309)' : 'var(--text-tertiary)' }}>{dupes.length}</p>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>Duplicates found — decide below</p>
                    </div>
                  </div>

                  {dupes.length === 0 ? (
                    <div className="alert-success">
                      <CheckCircle className="h-4 w-4 shrink-0" />
                      <span>No duplicates found — all {uniqueCount} records are new.</span>
                    </div>
                  ) : (
                    <>
                      {/* Bulk action bar */}
                      <div className="flex items-center gap-2 p-3 rounded-lg" style={{ background: 'var(--surface-raised)', border: '1px solid var(--border-default)' }}>
                        <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Apply to all {dupes.length} duplicates:</span>
                        <div className="flex gap-1.5 ml-2">
                          {(Object.entries(ACTION_LABELS) as [DedupAction, typeof ACTION_LABELS[DedupAction]][]).map(([action, meta]) => (
                            <button
                              key={action}
                              onClick={() => applyBulkAction(action)}
                              className={bulkAction === action ? 'btn-primary' : 'btn-secondary'}
                              style={{ height: 30, fontSize: 12, padding: '0 10px', gap: 4 }}
                            >
                              {meta.icon} {meta.label} all
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Per-record table */}
                      <div className="table-card overflow-hidden">
                        <table className="data-table" style={{ fontSize: 12 }}>
                          <thead>
                            <tr>
                              <th style={{ width: 50 }}>Row</th>
                              <th>Incoming record</th>
                              <th>Existing record in CRM</th>
                              <th style={{ width: 160 }}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dupes.map(d => {
                              const action = rowActions[d.row_index] ?? 'update'
                              return (
                                <tr key={d.row_index}>
                                  <td className="tabular-nums text-center" style={{ color: 'var(--text-tertiary)' }}>
                                    {d.row_index + 1}
                                  </td>
                                  {/* Incoming */}
                                  <td>
                                    <p className="font-medium" style={{ color: 'var(--text-primary)' }}>{d.incoming_name ?? '—'}</p>
                                    <p style={{ color: 'var(--text-tertiary)' }}>{d.incoming_mobile}</p>
                                    {d.incoming_vehicle && <p style={{ color: 'var(--text-tertiary)' }}>{d.incoming_vehicle}</p>}
                                  </td>
                                  {/* Existing */}
                                  <td>
                                    <p className="font-medium" style={{ color: 'var(--text-primary)' }}>{d.existing.customer_name}</p>
                                    <p style={{ color: 'var(--text-tertiary)' }}>{d.existing.vehicle_reg_no ?? '—'}</p>
                                    <span className="badge-neutral" style={{ fontSize: 10 }}>{d.existing.status.replace(/_/g, ' ')}</span>
                                  </td>
                                  {/* Action selector */}
                                  <td>
                                    <div className="flex gap-1">
                                      {(Object.entries(ACTION_LABELS) as [DedupAction, typeof ACTION_LABELS[DedupAction]][]).map(([act, meta]) => (
                                        <button
                                          key={act}
                                          title={meta.label}
                                          onClick={() => setRowActions(prev => ({ ...prev, [d.row_index]: act }))}
                                          className={action === act ? 'btn-primary' : 'btn-secondary'}
                                          style={{ height: 26, fontSize: 11, padding: '0 7px', gap: 3 }}
                                        >
                                          {meta.icon}
                                          <span className="hidden sm:inline">{meta.label}</span>
                                        </button>
                                      ))}
                                    </div>
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Action summary */}
                      <div className="flex gap-3 text-xs" style={{ color: 'var(--text-tertiary)' }}>
                        {(['skip', 'update', 'create'] as DedupAction[]).map(act => {
                          const count = dupes.filter(d => (rowActions[d.row_index] ?? 'update') === act).length
                          if (!count) return null
                          return <span key={act}>{ACTION_LABELS[act].label}: <strong>{count}</strong></span>
                        })}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── STEP 4: Options ─────────────────────────────────────────────── */}
          {step === 'options' && (
            <div className="space-y-4">
              <div className="form-group">
                <label className="form-label">Default lead source</label>
                <select className="input" value={defaultSource} onChange={e => setDefaultSource(e.target.value as LeadSource)}>
                  {Object.values(LeadSource).map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                </select>
                <p className="form-hint">Applied to every row that doesn't have its own Lead Source column value.</p>
              </div>

              <div className="form-group">
                <label className="form-label">Date format (Insurance Due Date column)</label>
                <select className="input" value={dateFormat} onChange={e => setDateFormat(e.target.value as 'auto' | 'DMY' | 'MDY')}>
                  <option value="auto">Auto-detect</option>
                  <option value="DMY">DD/MM/YYYY — Indian format (e.g. 28/07/2021)</option>
                  <option value="MDY">MM/DD/YYYY — US format (e.g. 7/28/2021)</option>
                </select>
                <p className="form-hint">Select MM/DD/YYYY if your file uses US-style dates or was exported from Excel.</p>
              </div>

              {/* Import summary */}
              <div className="p-4 rounded-lg space-y-1" style={{ background: 'var(--surface-raised)', border: '1px solid var(--border-default)' }}>
                <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Import summary</p>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{uniqueCount} new records will be created</p>
                {dupes.length > 0 && (['skip', 'update', 'create'] as DedupAction[]).map(act => {
                  const count = dupes.filter(d => (rowActions[d.row_index] ?? 'update') === act).length
                  if (!count) return null
                  return <p key={act} className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    {count} duplicate{count > 1 ? 's' : ''} → {ACTION_LABELS[act].label}
                  </p>
                })}
                <p className="text-sm font-medium pt-1" style={{ color: 'var(--text-primary)' }}>
                  Total records to process: {parsedRows.length - dupes.filter(d => (rowActions[d.row_index] ?? 'update') === 'skip').length}
                </p>
              </div>

              {uploadError && (
                <div className="alert-danger">
                  <AlertCircle className="h-4 w-4 shrink-0" /><span>{uploadError}</span>
                </div>
              )}
            </div>
          )}

          {/* ── STEP 5: Result ───────────────────────────────────────────────── */}
          {step === 'result' && result && (
            <div className="space-y-4">
              <div className={result.success === 0 ? 'alert-danger' : result.failures.length === 0 ? 'alert-success' : 'alert-warning'}>
                {result.success === 0
                  ? <AlertCircle className="h-5 w-5 shrink-0" />
                  : <CheckCircle className="h-5 w-5 shrink-0" />
                }
                <div>
                  <p className="font-semibold">
                    {result.success === 0
                      ? `Import failed — all ${result.total} rows could not be processed`
                      : `${result.success} of ${result.total} records imported${result.failures.length > 0 ? `, ${result.failures.length} failed` : ''}`
                    }
                  </p>
                  <p className="text-sm mt-0.5 opacity-80">Batch ID: #{result.batch_id}</p>
                </div>
              </div>

              {(result.warnings?.length ?? 0) > 0 && (
                <div className="space-y-2">
                  {(result.warnings ?? []).map((w, i) => (
                    <div key={i} className="alert-warning">
                      <AlertCircle className="h-4 w-4 shrink-0" />
                      <span className="text-sm">{w}</span>
                    </div>
                  ))}
                </div>
              )}

              {result.failures.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Skipped rows ({result.failures.length})</p>
                  <div className="table-card overflow-hidden">
                    <table className="data-table">
                      <thead><tr><th style={{ width: 80 }}>Row</th><th>Reason</th></tr></thead>
                      <tbody>
                        {result.failures.slice(0, 50).map((f, i) => (
                          <tr key={i}>
                            <td className="tabular-nums">{f.row}</td>
                            <td style={{ color: 'var(--danger-text)', fontSize: 12 }}>{f.error}</td>
                          </tr>
                        ))}
                        {result.failures.length > 50 && (
                          <tr><td colSpan={2} className="text-center text-sm py-2" style={{ color: 'var(--text-tertiary)' }}>…and {result.failures.length - 50} more rows</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="modal-flex-footer justify-between">
          <div>
            {step !== 'pick' && step !== 'result' && (
              <button className="btn-secondary" disabled={uploading || dupesLoading}
                onClick={() => setStep(STEPS[stepIndex - 1])}>
                <ChevronLeft className="h-3.5 w-3.5" /> Back
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {step === 'result' && (
              <button
                className="btn-primary"
                onClick={() => { if (result && result.success > 0) onComplete(); onClose() }}
              >
                Done
              </button>
            )}
            {step === 'options' && (
              <button className="btn-primary" onClick={handleUpload} disabled={uploading}>
                {uploading
                  ? <><Loader className="h-3.5 w-3.5 animate-spin" /> Uploading…</>
                  : <><Upload className="h-3.5 w-3.5" /> Upload {file?.name}</>
                }
              </button>
            )}
            {step === 'dupes' && !dupesLoading && (
              <button className="btn-primary" onClick={() => setStep('options')}>
                Continue <ChevronRight className="h-3.5 w-3.5" />
              </button>
            )}
            {step === 'map' && (
              <button className="btn-primary" disabled={!mappingComplete() || dupesLoading} onClick={() => proceedToDupeCheck()}>
                {dupesLoading
                  ? <><Loader className="h-3.5 w-3.5 animate-spin" /> Checking…</>
                  : <>Continue <ChevronRight className="h-3.5 w-3.5" /></>
                }
              </button>
            )}
          </div>
        </div>

      </div>
    </ModalOverlay>
  )
}
